import { useAuthStore } from '../stores/auth'

export type OAuthProvider = 'google' | 'github'

export const SERVER_URL = import.meta.env.VITE_SERVER_URL || 'https://airi-api.moeru.ai'

// JWT token management
const ACCESS_TOKEN_KEY = 'airi_access_token'
const REFRESH_TOKEN_KEY = 'airi_refresh_token'

export function getAuthToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

export function setAuthTokens(accessToken: string, refreshToken?: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  if (refreshToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

export function clearAuthTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = getAuthToken()
  const headers = new Headers(options.headers)
  if (token)
    headers.set('Authorization', `Bearer ${token}`)
  if (!headers.has('Content-Type') && options.body)
    headers.set('Content-Type', 'application/json')

  return fetch(`${SERVER_URL}${path}`, { ...options, headers })
}

export async function fetchSession(): Promise<boolean> {
  const token = getAuthToken()
  if (!token)
    return false

  try {
    const res = await apiFetch('/api/auth/get-session')
    if (!res.ok) {
      // Try to refresh token
      const refreshed = await refreshAccessToken()
      if (!refreshed) {
        clearAuthTokens()
        return false
      }
      // Retry with new token
      const retryRes = await apiFetch('/api/auth/get-session')
      if (!retryRes.ok) {
        clearAuthTokens()
        return false
      }
      const data = await retryRes.json()
      const authStore = useAuthStore()
      authStore.user = data.user
      authStore.session = data.session
      return true
    }

    const data = await res.json()
    const authStore = useAuthStore()
    authStore.user = data.user
    authStore.session = data.session
    return true
  }
  catch {
    return false
  }
}

export async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken)
    return false

  try {
    const headers = new Headers()
    headers.set('Authorization', `Bearer ${refreshToken}`)
    headers.set('Content-Type', 'application/json')

    const res = await fetch(`${SERVER_URL}/api/auth/token/refresh`, {
      method: 'POST',
      headers,
    })

    if (!res.ok)
      return false

    const data = await res.json()
    if (data.token) {
      setAuthTokens(data.token)
      return true
    }
    return false
  }
  catch {
    return false
  }
}

export async function signUpWithEmail(name: string, email: string, password: string) {
  const res = await fetch(`${SERVER_URL}/api/auth/sign-up/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.message || 'Sign up failed')
  }

  const data = await res.json()
  setAuthTokens(data.token, data.refreshToken)

  const authStore = useAuthStore()
  authStore.user = data.user
  authStore.session = data.session

  return data
}

export async function signInWithEmail(email: string, password: string) {
  const res = await fetch(`${SERVER_URL}/api/auth/sign-in/email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  if (!res.ok) {
    const error = await res.json()
    throw new Error(error.message || 'Sign in failed')
  }

  const data = await res.json()
  setAuthTokens(data.token, data.refreshToken)

  const authStore = useAuthStore()
  authStore.user = data.user
  authStore.session = data.session

  return data
}

export async function listSessions() {
  // In JWT mode, we only have the current session
  const token = getAuthToken()
  if (!token)
    return { data: null }

  try {
    const res = await apiFetch('/api/auth/get-session')
    if (!res.ok)
      return { data: null }
    const data = await res.json()
    return { data: data.session ? [data.session] : [] }
  }
  catch {
    return { data: null }
  }
}

export async function signOut() {
  try {
    await apiFetch('/api/auth/sign-out', { method: 'POST' })
  }
  catch {
    // Ignore errors, clear local state anyway
  }

  clearAuthTokens()

  const authStore = useAuthStore()
  authStore.user = undefined
  authStore.session = undefined
}

export async function signIn(provider: OAuthProvider) {
  // OAuth: redirect to the social sign-in endpoint
  window.location.href = `${SERVER_URL}/api/auth/sign-in/social?provider=${provider}`
}
